import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import { Box, Stack, Typography } from '@mui/material'
import type {
  ShearAnchorCheckDTO,
  ShearAnchorConnectorSelectionDTO,
  ShearAnchorLoadsDTO,
  ShearAnchorPosition,
} from '@api/fastAPI.schemas'
import { useGetAllProjectShearConnections } from '@api/project-shear-connections/project-shear-connections'
import { postShearAnchorCheckPreview } from '@api/shear-anchor-connector-selections/shear-anchor-connector-selections'
import useResultsSectionOpen from '@editor/pages/Results2/hooks/useResultsSectionOpen'
import { useQuery } from '@tanstack/react-query'
import { LabelledValueRow, Section } from '@ui2/structure'
import { Tabs as DetailTabs } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { useStiffeningStore } from '@editorStores'
import { useBlockAction, useGuidToElement, useSceneElement } from '@editorHooks'
import { useElementLabel, useResultsSelection } from '@editorHooks'
import {
  SHEAR_ANCHOR_POSITION_LABEL,
  SHEAR_CONNECTION_CATEGORY_LABEL,
  SHEAR_CONNECTION_CATEGORY_PLACEMENT_LABEL,
} from 'src/components/manager/shearConnections'
import { buildErrorMessage } from 'src/constants/errors'
import { ConnectorSelection, isSameConnectorSelection } from '../../connectorSelection'
import useAnchorsOfSegment from '../../hooks/useAnchorsOfSegment'
import useDesignFormNavigationGuard from '../../hooks/useDesignFormNavigationGuard'
import useSaveShearAnchorSelections from '../../hooks/useSaveShearAnchorSelections'
import ConnectorSelectionSelect from '../ConnectorSelectionSelect'
import Results2DetailCloseButton from '../Results2DetailCloseButton'
import type { Results2RelationLink } from '../Results2DetailPane/components/Results2RelationLinksRow'
import { RESULTS2_DESIGN_FORM_BLOCK_ACTION } from '../Results2DetailPane/constants'
import Results2PaneHeader from '../Results2PaneHeader'
import ShearAnchorBandSceneMarker from './components/ShearAnchorBandSceneMarker'
import ShearAnchorLoadResults from './components/ShearAnchorLoadResults'
import ShearAnchorSaveRow from './components/ShearAnchorSaveRow'
import ShearFlowCheckTable from './components/ShearFlowCheckTable'
import { formatMeters, getShearAnchorBandPoints, savedShearAnchorSelection } from './utils'

interface Props {
  /** Stiffening segment the checks belong to. */
  segmentGuid: string
  /** Which of the segment's two shear anchors is shown: the one at the wall's foot or head. */
  position: ShearAnchorPosition
  /** All of the segment's checks; only those at `position` are shown. */
  checks: ShearAnchorCheckDTO[]
  loads?: ShearAnchorLoadsDTO
  /** How the segment's Schubverbinder are chosen, per position. */
  selections: ShearAnchorConnectorSelectionDTO[]
  /** Disables edits while a list autosave is dirty or in flight. */
  disableForm?: boolean
}

interface DraftState {
  /** The saved selection the draft was initialized from — used to detect refetched data. */
  base: ConnectorSelection
  selection: ConnectorSelection
}

/**
 * Detail view of the "Schubanker" wall sub-tab: how the design shear flow of one stiffening
 * segment is derived per load category, which shear anchor was chosen at the selected end of the
 * wall (its foot or its head), and the resulting utilization. The Schubverbinder can be fixed by
 * hand -- previewed live, saved via the footer. The other end's anchor is one relation link away.
 */
const Results2ShearAnchorDetailPane = ({
  segmentGuid,
  position,
  checks,
  loads,
  selections,
  disableForm = false,
}: Props): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const getElementLabel = useElementLabel()
  const guardDesignFormNav = useDesignFormNavigationGuard()
  const {
    selectResultsPosition: selectPosition,
    selectResultsStiffeningSegment: selectStiffeningSegment,
    selectResultsAnchor: selectAnchor,
    selectResultsShearAnchor: selectShearAnchor,
  } = useResultsSelection()
  const anchorsOfSegment = useAnchorsOfSegment(segmentGuid)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const guidToElement = useGuidToElement()
  const generalSection = useResultsSectionOpen('results2.shear-anchor.general')
  const derivationSection = useResultsSectionOpen('results2.shear-anchor.derivation')
  const loadsSection = useResultsSectionOpen('results2.shear-anchor.loads')

  const title = `${getElementLabel(segmentGuid) || segmentGuid} · ${
    SHEAR_ANCHOR_POSITION_LABEL[position]
  }`
  const otherPosition: ShearAnchorPosition = position === 'bottom' ? 'top' : 'bottom'

  const savedChecksAtPosition = useMemo(
    () => checks.filter(check => check.position === position),
    [checks, position],
  )

  // ----- Schubverbinder draft (re-based whenever the saved selection changes) -----

  const { data: shearConnections = [] } = useGetAllProjectShearConnections(projectId as string, {
    query: {
      enabled: !!projectId,
      onError: (error: unknown) =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Schubverbinder'),
          { variant: 'error' },
        ),
    },
  })
  const savedSelection = useMemo(
    () => savedShearAnchorSelection(selections, position),
    [selections, position],
  )
  const [draftState, setDraftState] = useState<DraftState | null>(null)
  if (draftState === null) {
    setDraftState({ base: savedSelection, selection: savedSelection })
  } else if (!isSameConnectorSelection(draftState.base, savedSelection)) {
    // A refetch re-anchors the base; a selection the user changed locally stays.
    const editedLocally = !isSameConnectorSelection(draftState.selection, draftState.base)
    setDraftState({
      base: savedSelection,
      selection: editedLocally ? draftState.selection : savedSelection,
    })
  }
  const selection = draftState?.selection ?? savedSelection
  const selectionDirty = !isSameConnectorSelection(selection, savedSelection)

  const { saveShearAnchorSelections, isSaving } = useSaveShearAnchorSelections()
  const saveSelection = useCallback(
    () => saveShearAnchorSelections([{ guid: segmentGuid, position }], selection),
    [position, saveShearAnchorSelections, segmentGuid, selection],
  )
  const revertSelection = useCallback(
    () => setDraftState({ base: savedSelection, selection: savedSelection }),
    [savedSelection],
  )

  // Reusing the Bemessung block action makes the existing guards (tab switch, list selection,
  // esc, reload) ask for the save first.
  useBlockAction(RESULTS2_DESIGN_FORM_BLOCK_ACTION, selectionDirty ? 'provider' : undefined)

  // Live preview of the checks with the draft Schubverbinder, computed by the backend without
  // storing anything.
  const previewQuery = useQuery({
    queryKey: [
      'shear-anchor-check-preview',
      projectId,
      segmentGuid,
      position,
      selection.policy,
      selection.connectorId,
    ],
    queryFn: ({ signal }) =>
      postShearAnchorCheckPreview(
        projectId as string,
        {
          segmentId: segmentGuid,
          position,
          connectorSelectionPolicy: selection.policy,
          userDefinedConnectorId: selection.connectorId,
        },
        signal,
      ),
    enabled: !!projectId && selectionDirty,
    keepPreviousData: true,
    // The preview depends on loads and Schubverbinder that are not in the key, so it lives only
    // as long as the pane rather than being served from the cache later.
    staleTime: 0,
    cacheTime: 0,
    onError: (error: unknown) =>
      enqueueSnackbar(
        buildErrorMessage(error as AxiosError, 'Fehler beim Ermitteln der Schubanker-Vorschau'),
        { variant: 'error' },
      ),
  })
  // A previous pick's preview is never shown as the current one's.
  const isPreviewing = selectionDirty && !!previewQuery.data && !previewQuery.isPreviousData
  const checksAtPosition = useMemo(
    () =>
      isPreviewing && previewQuery.data
        ? previewQuery.data.checks.filter(check => check.position === position)
        : savedChecksAtPosition,
    [isPreviewing, position, previewQuery.data, savedChecksAtPosition],
  )
  const usedAnchor = savedChecksAtPosition[0]?.used_anchor

  const segment = useMemo(
    () => find(stiffeningIntervals, { guid: segmentGuid }),
    [stiffeningIntervals, segmentGuid],
  )
  // Same lookup `getWallGuidForStiffeningSegment` does; the segment is already here.
  const linkedWallGuid = segment?.element_guid

  const wallPoints = useMemo(() => {
    const element = segment ? (guidToElement[segment.element_guid] as ShapeObject) : undefined
    const points = element?.shape?.points
    if (!points || points.length < 2) return undefined
    return points
  }, [guidToElement, segment])
  const wallLength = wallPoints ? wallPoints[0].distanceTo(wallPoints[1]) : undefined

  // Stiffening intervals are absolute meters along the wall (same interpretation as the scene's
  // SimpleStiffeningMesh) -- no scaling by the wall length.
  const segmentStart = segment ? parseFloat(segment.interval.lower) : undefined
  const segmentEnd = segment ? parseFloat(segment.interval.upper) : undefined
  const segmentLength =
    segmentStart !== undefined && segmentEnd !== undefined ? segmentEnd - segmentStart : undefined

  // Marks the segment's wall foot or wall head on its wall in the scene.
  const bandProperties = useMemo(() => {
    if (!wallPoints || !segment || segmentStart === undefined || segmentEnd === undefined) {
      return { points: [] }
    }
    const points = getShearAnchorBandPoints(
      wallPoints,
      segmentStart,
      segmentEnd,
      segment.effective_height,
      position,
    )
    return { points: points ?? [] }
  }, [position, segment, segmentEnd, segmentStart, wallPoints])
  useSceneElement({
    enabled: bandProperties.points.length > 0,
    properties: bandProperties,
    component: ShearAnchorBandSceneMarker,
  })

  const handleSelectLinkedWall = useCallback(() => {
    if (!linkedWallGuid) return
    if (guardDesignFormNav()) return
    selectPosition(linkedWallGuid, 'walls', 'wall-assemblies')
  }, [guardDesignFormNav, linkedWallGuid, selectPosition])

  const handleSelectSegment = useCallback(() => {
    if (guardDesignFormNav()) return
    selectStiffeningSegment(segmentGuid)
  }, [guardDesignFormNav, segmentGuid, selectStiffeningSegment])

  const relationLinks = useMemo((): Results2RelationLink[] => {
    const links: Results2RelationLink[] = [
      {
        // The segment's other shear anchor: the one at the opposite end of the wall.
        label: `zum Schubanker ${SHEAR_ANCHOR_POSITION_LABEL[otherPosition]}`,
        tooltip: `Schubanker-Nachweis am ${SHEAR_ANCHOR_POSITION_LABEL[otherPosition]} öffnen`,
        onClick: () => {
          if (guardDesignFormNav()) return
          selectShearAnchor(segmentGuid, otherPosition)
        },
      },
      ...anchorsOfSegment.map(({ supportGuid, side }) => ({
        label: side === 'rechts' ? 'zum rechten Zuganker' : 'zum linken Zuganker',
        tooltip: 'Zuganker-Nachweis öffnen',
        onClick: () => {
          if (guardDesignFormNav()) return
          selectAnchor(supportGuid)
        },
      })),
    ]
    links.push({
      label: 'zur Wandscheibe',
      tooltip: 'Wandscheiben-Nachweis öffnen',
      onClick: handleSelectSegment,
    })
    if (linkedWallGuid) {
      links.push({
        label: 'zur Wand',
        tooltip: 'Wand auswählen',
        onClick: handleSelectLinkedWall,
      })
    }
    return links
  }, [
    anchorsOfSegment,
    guardDesignFormNav,
    handleSelectLinkedWall,
    handleSelectSegment,
    linkedWallGuid,
    otherPosition,
    segmentGuid,
    selectAnchor,
    selectShearAnchor,
  ])

  // The system manager category whose prioritized shear anchor list the chosen anchor was taken
  // from; the same for every load category at this position.
  const category = checksAtPosition[0]?.category

  const subtitle = useMemo(() => {
    // The storey category decides which prioritized shear anchor list is used, so it belongs
    // in the header rather than buried in the table. Without the position half, which the title
    // already states.
    return [
      'Schubanker',
      category ? SHEAR_CONNECTION_CATEGORY_PLACEMENT_LABEL[category] : undefined,
    ]
      .filter(Boolean)
      .join(' · ')
  }, [category])

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <DetailTabs
          items={[{ id: 'design', label: 'Bemessung' }]}
          value="design"
          size="md"
          kind="folder"
        />
        <Results2DetailCloseButton />
      </Box>
      <Results2PaneHeader title={title} subtitle={subtitle} relationLinks={relationLinks} />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2 }}>
        <Stack spacing={3} sx={{ py: 1.5 }}>
          <Section title="Allgemein" collapsible hideBorderBottom {...generalSection}>
            <LabelledValueRow
              items={[
                // No left/right anchor here: a shear anchor runs along the whole
                // segment; which end of the wall it sits at is in the title.
                { label: 'Wand', value: linkedWallGuid ? getElementLabel(linkedWallGuid) : '-' },
                {
                  label: 'Wandlänge',
                  value: wallLength === undefined ? undefined : formatMeters(wallLength),
                },
                {
                  label: 'Segmentlänge',
                  value: segmentLength === undefined ? undefined : formatMeters(segmentLength),
                },
                {
                  label: 'Segmenthöhe',
                  value: segment ? formatMeters(segment.effective_height) : undefined,
                },
                {
                  label: 'Segment im Wandverlauf',
                  value:
                    segmentStart === undefined || segmentEnd === undefined
                      ? undefined
                      : `${formatMeters(segmentStart)} – ${formatMeters(segmentEnd)}`,
                },
                {
                  // The full label the system manager titles the prioritization list with,
                  // so the user can find the list the anchor was chosen from.
                  label: 'Kategorie',
                  value: category ? SHEAR_CONNECTION_CATEGORY_LABEL[category] : undefined,
                },
              ]}
            />
          </Section>
          <Section
            title="Bemessungs-Schubfluss & Nachweis"
            collapsible
            hideBorderBottom
            {...derivationSection}
          >
            <Box sx={{ pt: 0.5, pb: 1 }}>
              <ConnectorSelectionSelect
                value={selection}
                onChange={next => next && setDraftState({ base: savedSelection, selection: next })}
                connections={shearConnections}
                label="Schubanker"
                dataCy="shear-anchor-connector-selection"
                disabled={disableForm || isSaving}
              />
            </Box>
            {usedAnchor ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                Gewählt: <b>{usedAnchor.short_name}</b> – {usedAnchor.name}
                {savedSelection.policy === 'UserDefined' ? ' (manuell festgelegt)' : ''}
              </Typography>
            ) : undefined}
            {isPreviewing ? (
              <Typography
                variant="caption"
                color="warning.main"
                display="block"
                data-cy="shear-anchor-preview-hint"
              >
                Vorschau mit dem gewählten Schubanker – noch nicht gespeichert
              </Typography>
            ) : undefined}
            <ShearFlowCheckTable checks={checksAtPosition} />
          </Section>
          <Section title="Einwirkungen" collapsible hideBorderBottom {...loadsSection}>
            <ShearAnchorLoadResults
              loads={loads}
              elementGuid={segmentGuid}
              frictionCoefficient={checksAtPosition[0]?.shear_flow_data.friction_coefficient}
            />
          </Section>
        </Stack>
      </Box>
      <ShearAnchorSaveRow
        isSaving={isSaving}
        isDirty={selectionDirty}
        disabled={disableForm}
        onSave={saveSelection}
        onRevert={revertSelection}
      />
    </Box>
  )
}

export default Results2ShearAnchorDetailPane
